import { useState, useCallback } from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Radio, Webhook, Copy, Check, RefreshCw, ShieldCheck, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Public-facing host for webhook URLs shown in the UI. Backend can live on a
// different internal host — only the *displayed* URL is rewritten so users
// see the product domain (which is what they'll actually paste into
// TradingView once DNS is finalized).
const PUBLIC_WEBHOOK_HOST = 'https://tradingpilot360.com';

/**
 * Dual-mode signal-source control:
 *   - Default: System signals feed (zero setup, beginner-friendly)
 *   - Advanced: Custom TradingView webhook (user's own alerts)
 *
 * Safety reminder is always visible in advanced mode — risk engine (SL,
 * risk %, daily drawdown, loss-streak pause) stays active in both modes.
 */
export function SignalSourcePanel({ settings, token, onUpdated }) {
  const mode = settings?.signal_mode || 'system';
  const isCustom = mode === 'custom';
  const [advancedOpen, setAdvancedOpen] = useState(isCustom);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [switching, setSwitching] = useState(false);

  const fetchWebhookInfo = useCallback(async () => {
    if (!token || webhookInfo) return;
    setLoadingInfo(true);
    try {
      const res = await fetch(`${API_URL}/api/paper-trading/webhook-info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setWebhookInfo(await res.json());
    } finally {
      setLoadingInfo(false);
    }
  }, [token, webhookInfo]);

  const openAdvanced = async () => {
    setAdvancedOpen(true);
    await fetchWebhookInfo();
  };

  const persistMode = async (nextMode) => {
    if (!token) return;
    setSwitching(true);
    try {
      const res = await fetch(`${API_URL}/api/paper-trading/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled: settings.enabled,
          max_trades_per_day: settings.max_trades_per_day,
          emergency_stop: settings.emergency_stop,
          min_score_threshold: settings.min_score_threshold,
          signal_mode: nextMode,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(
        nextMode === 'custom'
          ? 'Custom signals enabled — paste the webhook URL into TradingView, then switch Automation back on.'
          : 'Switched back to System signals.'
      );
      if (nextMode === 'custom' && !webhookInfo) await fetchWebhookInfo();
      onUpdated?.();
    } catch {
      toast.error('Could not change signal mode. Please retry.');
    } finally {
      setSwitching(false);
    }
  };

  const rotateToken = async () => {
    if (!token) return;
    if (!window.confirm('Rotate your webhook URL? Any existing TradingView alert using the old URL will stop working.')) return;
    try {
      const res = await fetch(`${API_URL}/api/paper-trading/webhook-rotate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setWebhookInfo((prev) => ({ ...(prev || {}), ...data }));
      toast.success('Webhook URL rotated. Update your TradingView alert.');
    } catch {
      toast.error('Could not rotate webhook URL.');
    }
  };

  const fullWebhookUrl = webhookInfo
    ? `${PUBLIC_WEBHOOK_HOST}${webhookInfo.webhook_path || ''}`
    : '';

  const copyUrl = async () => {
    if (!fullWebhookUrl) return;
    try {
      await navigator.clipboard.writeText(fullWebhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — select and copy manually.');
    }
  };

  return (
    <div data-testid="signal-source-panel">
      {/* Status row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {isCustom ? (
            <Webhook className="w-4 h-4 text-primary shrink-0" />
          ) : (
            <Radio className="w-4 h-4 text-success shrink-0" />
          )}
          <p
            className="text-xs font-semibold text-foreground truncate"
            data-testid="signal-source-status"
          >
            {isCustom ? 'Custom TradingView alerts' : 'System signals active'}
          </p>
        </div>
        {!advancedOpen && !isCustom && (
          <button
            type="button"
            onClick={openAdvanced}
            className="text-[11px] font-medium text-primary hover:underline whitespace-nowrap"
            data-testid="signal-source-advanced-link"
          >
            Advanced: use your own TradingView alerts →
          </button>
        )}
      </div>

      {/* Advanced panel */}
      {(advancedOpen || isCustom) && (
        <div
          className="mt-3 p-3 rounded-md border border-border/40 bg-muted/30 space-y-3"
          data-testid="signal-source-advanced-panel"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-foreground">Use my own TradingView alerts</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Route your personal TradingView webhook alerts into this sandbox.
                {!isCustom && ' Automation will be paused until you re-enable it after configuring TradingView.'}
              </p>
            </div>
            <Switch
              checked={isCustom}
              onCheckedChange={(v) => persistMode(v ? 'custom' : 'system')}
              disabled={switching}
              data-testid="signal-source-switch"
            />
          </div>

          {isCustom && (
            <>
              {loadingInfo && (
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading your webhook URL…
                </div>
              )}
              {webhookInfo && (
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground mb-1">
                    Your personal webhook URL (paste into TradingView → Alerts → Webhook):
                  </p>
                  <div className="flex items-stretch gap-2">
                    <code
                      className="flex-1 px-2 py-1.5 rounded bg-background border border-border/40 text-[11px] font-mono truncate"
                      data-testid="signal-source-webhook-url"
                      title={fullWebhookUrl}
                    >
                      {fullWebhookUrl}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyUrl}
                      data-testid="signal-source-copy-btn"
                      className="h-7 px-2"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={rotateToken}
                      data-testid="signal-source-rotate-btn"
                      className="h-7 px-2"
                      title="Rotate webhook URL"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    POST JSON: {'{'} &quot;symbol&quot;, &quot;price&quot;, &quot;timeframe&quot;, &quot;signal&quot; (BUY/SELL) {'}'}
                  </p>
                </div>
              )}

              {/* Safety reminder — non-optional, always visible in custom mode */}
              <div
                className="flex items-start gap-2 p-2 rounded bg-success/5 border border-success/20"
                data-testid="signal-source-safety-note"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-success mt-0.5 shrink-0" />
                <p className="text-[10px] leading-relaxed text-muted-foreground">
                  Risk engine stays on in both modes — SL/TP, risk %, daily loss cap, and loss-streak
                  pause cannot be disabled.
                </p>
              </div>
            </>
          )}

          {!isCustom && advancedOpen && (
            <button
              type="button"
              onClick={() => setAdvancedOpen(false)}
              className="text-[11px] text-muted-foreground hover:text-foreground"
              data-testid="signal-source-hide-link"
            >
              Hide advanced
            </button>
          )}
        </div>
      )}
    </div>
  );
}
